import { dateTimeFormat } from '@magicdoorinc/date';
import { useLocalization } from '@magicdoorinc/ui';
import { LeaseStatus, LeaseChargeLedgerEntryStatus } from '~/swagger/portal/Api';
import { emptyPlaceholder } from '~/utils/constant';
import { parseKeyBasedDescription } from '~/utils/jsonDescriptions';
import type { HydratedLeaseDto, HydratedUnitDto, LeaseDto } from '~/swagger/portal/Api';
import type { DescriptionValue } from '~/utils/jsonDescriptions';

export const isMonthToMonth = (lease: HydratedLeaseDto | LeaseDto | undefined) => lease?.status === LeaseStatus.MonthToMonth;

export const isEndedLease = (lease: HydratedLeaseDto | LeaseDto | undefined) => lease?.status === LeaseStatus.Ended;

export const isFutureLease = (lease: HydratedLeaseDto | LeaseDto | undefined) => lease?.status === LeaseStatus.Future;
export const isEvictionLease = (lease: HydratedLeaseDto | LeaseDto | undefined) => lease?.status === LeaseStatus.Eviction;
export const isLeased = (lease: HydratedLeaseDto | LeaseDto | undefined) =>
  lease?.status && [LeaseStatus.Lease, LeaseStatus.LeaseMonthToMonth].includes(lease?.status);
export const isExpiredLease = (lease: HydratedLeaseDto | LeaseDto | undefined) => lease?.status === LeaseStatus.Expired;
export const isDraftLease = (lease: HydratedLeaseDto | LeaseDto | undefined) => lease?.status === LeaseStatus.Draft;

export const leaseStatusText = (lease: HydratedLeaseDto | undefined) => {
  const { t } = useLocalization();
  if (!lease) return t('Loading');
  return leaseStatusString(lease.status);
};

export const leaseStatusString = (status: LeaseStatus | undefined) => {
  const { t } = useLocalization();
  switch (status) {
    case LeaseStatus.Ended:
      return t('Ended lease');
    case LeaseStatus.Future:
      return t('Coming up');
    case LeaseStatus.MonthToMonth:
      return t('Month-to-month');
    case LeaseStatus.Eviction:
      return t('Eviction');
    case LeaseStatus.Expired:
      return t('Expired');
    case LeaseStatus.Draft:
      return t('Draft');
    case LeaseStatus.LeaseMonthToMonth:
    case LeaseStatus.Lease:
      return t('Leased');
    default:
      return emptyPlaceholder;
  }
};

export const getLeaseTransactionStatusString = (status?: LeaseChargeLedgerEntryStatus) => {
  const { t } = useLocalization();
  switch (status) {
    case LeaseChargeLedgerEntryStatus.Future:
      return t('Future');
    case LeaseChargeLedgerEntryStatus.Due:
      return t('Due');
    case LeaseChargeLedgerEntryStatus.PastDue:
      return t('Past due');
    case LeaseChargeLedgerEntryStatus.Paid:
      return t('Paid');
    case LeaseChargeLedgerEntryStatus.Failed:
      return t('Failed');
    case LeaseChargeLedgerEntryStatus.Returned:
      return t('Returned');
    case LeaseChargeLedgerEntryStatus.Processing:
      return t('Processing');
    case LeaseChargeLedgerEntryStatus.Settled:
      return t('Settled');
    default:
      return emptyPlaceholder;
  }
};
export const leasePeriodString = (lease: HydratedLeaseDto | LeaseDto) => {
  const arr = isMonthToMonth(lease) ? [lease?.start] : [lease?.start, lease?.end];
  return arr
    .filter((e) => e)
    .map((date) => dateTimeFormat(date, { timeFormat: false }))
    .join(' - ');
};

export const getLeaseFullname = (lease?: HydratedLeaseDto) => {
  return lease && lease?.property
    ? `${lease?.property?.displayName || lease?.property?.address?.streetAddress1}, ${lease?.unit?.name}`
    : emptyPlaceholder;
};

export const getUnitFullname = (unit?: HydratedUnitDto) => {
  return unit && unit?.property
    ? `${unit?.property?.displayName || unit?.property?.address?.streetAddress1}, ${unit?.name}`
    : emptyPlaceholder;
};

export const getLeaseDateName = (lease: HydratedLeaseDto | undefined) => {
  if (!lease) return emptyPlaceholder;
  return `${lease.unit?.name}-[${leasePeriodString(lease)}]`;
};

export const getDescription = (description: DescriptionValue): string => {
  return parseKeyBasedDescription(description);
};
